/**
 * Dialogue modal pour ajuster automatiquement le calendrier du BT
 * quand l'estimation de jours change suite à une modification de l'équipe
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Calendar, Users, TrendingUp, TrendingDown } from 'lucide-react';

export const AjusterCalendrierDialog = ({ 
  open, 
  onClose, 
  ajustementData, 
  datesActuelles,
  onAjuster,
  btLies = []
}) => {
  const [ajusterBtsLies, setAjusterBtsLies] = useState(true);
  const [loading, setLoading] = useState(false);

  if (!ajustementData) return null;

  const { avant, apres, ecart, nouveauxJoursSuggeres } = ajustementData;
  const augmentation = ecart > 0;
  const joursAAjouter = Math.ceil(Math.abs(ecart));

  // Calculer les nouvelles dates suggérées
  const calculerNouvellesDates = () => {
    if (!datesActuelles || datesActuelles.length === 0) return [];
    
    const dates = [...datesActuelles].sort();
    const derniereDate = new Date(dates[dates.length - 1]);
    const nouvellesDates = [...dates];
    
    if (augmentation) {
      // Ajouter des jours à la fin
      for (let i = 1; i <= joursAAjouter; i++) {
        const nouvelleDate = new Date(derniereDate);
        nouvelleDate.setDate(derniereDate.getDate() + i);
        nouvellesDates.push(nouvelleDate.toISOString().split('T')[0]);
      }
    } else {
      // Retirer des jours à la fin
      nouvellesDates.splice(-joursAAjouter);
    }
    
    return nouvellesDates;
  };

  const nouvellesDates = calculerNouvellesDates();

  const handleAjuster = async () => {
    setLoading(true);
    try {
      await onAjuster({
        nouvellesDates,
        ajusterBtsLies
      });
      onClose();
    } catch (error) {
      console.error('Erreur ajustement:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            L'estimation de jours a changé
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Changement d'équipe */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-sm">Changement d'équipe</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Avant:</span>
                <span className="ml-2 font-semibold">{avant.nbMembres} employé(s)</span>
              </div>
              <div>
                <span className="text-slate-500">Maintenant:</span>
                <span className="ml-2 font-semibold">{apres.nbMembres} employé(s)</span>
              </div>
            </div>
          </div>

          {/* Impact sur l'estimation */}
          <div className={`border rounded-lg p-4 ${augmentation ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              {augmentation ? (
                <TrendingUp className="w-4 h-4 text-amber-600" />
              ) : (
                <TrendingDown className="w-4 h-4 text-green-600" />
              )}
              <span className="font-medium text-sm">Impact sur l'estimation</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500 block">Avant</span>
                <span className="font-semibold text-lg">{avant.jours.toFixed(1)} jour(s)</span>
              </div>
              <div className="flex items-center justify-center">
                <span className={`text-2xl ${augmentation ? 'text-amber-600' : 'text-green-600'}`}>
                  {augmentation ? '→' : '←'}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block">Maintenant</span>
                <span className="font-semibold text-lg">{apres.jours.toFixed(1)} jour(s)</span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-current/10">
              <span className={`font-medium ${augmentation ? 'text-amber-700' : 'text-green-700'}`}>
                {augmentation ? '⚠️ ' : '✓ '}
                Écart: {augmentation ? '+' : ''}{ecart.toFixed(1)} jour(s)
              </span>
            </div>
          </div>

          {/* Ajustement proposé */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-slate-600" />
              <span className="font-medium text-sm">Ajustement du calendrier</span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-slate-500">Calendrier actuel:</span>
                <span className="ml-2 font-medium">{avant.joursPlanifies} jour(s) planifié(s)</span>
              </div>
              <div>
                <span className="text-slate-500">Proposition:</span>
                <span className="ml-2 font-semibold text-blue-600">
                  {augmentation ? 'Rallonger' : 'Raccourcir'} de {joursAAjouter} jour(s)
                </span>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <span className="text-slate-500">Nouvelles dates:</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {nouvellesDates.map((date, i) => (
                    <span key={date.id || date._id || i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      {new Date(date + 'T00:00:00').toLocaleDateString('fr-CA', { 
                        day: 'numeric', 
                        month: 'short' 
                      })}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* BTs liés */}
          {btLies.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-sm">Bons de travail liés ({btLies.length})</span>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ajusterBtsLies}
                  onChange={(e) => setAjusterBtsLies(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-purple-300"
                />
                <div className="text-sm">
                  <div className="font-medium text-purple-900">Ajuster automatiquement les autres BTs</div>
                  <div className="text-purple-700 text-xs mt-1">
                    Les dates des {btLies.length} autre(s) BT(s) sur ce dossier seront également {augmentation ? 'rallongées' : 'raccourcies'}.
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Ignorer
          </Button>
          <Button onClick={handleAjuster} disabled={loading}>
            {loading ? 'Ajustement...' : 'Appliquer les ajustements'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
